import { ActionIcon, Button, Select, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { coordinatorLabel, type CoordinatorRole } from '@reditelji/shared';
import { useState } from 'react';
import { type CoordinatorRow, useCoordinatorMutations, useCoordinators } from '../api/coordinators';
import { useSession } from '../api/session';

const ROLE_OPTIONS: { value: CoordinatorRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'coordinator', label: 'Koordinator' },
  { value: 'organizer', label: 'Organizacija' },
];

const onDate = (iso: string) =>
  new Date(iso).toLocaleDateString('sl-SI', { day: 'numeric', month: 'numeric', year: 'numeric' });

/**
 * Who may log in, as what (SPEC §1.1L). Every change is one request that
 * settles when the server answers — there is nothing to save as a whole.
 */
export function UsersCard() {
  const { me } = useSession();
  const list = useCoordinators();
  const mut = useCoordinatorMutations();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<CoordinatorRole>('organizer');

  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  const add = () =>
    mut.grant.mutate(
      { email: email.trim(), name: name.trim(), role },
      {
        onSuccess: (row) => {
          notifications.show({
            color: 'green',
            message: `Dodano. ${row.email} se prijavi na ${window.location.origin}/login.`,
          });
          setEmail('');
          setName('');
        },
      },
    );

  return (
    <div className="st-card">
      <div className="st-eyebrow">UPORABNIKI</div>
      <div className="st-hint" style={{ marginBottom: 14 }}>
        Kdo se lahko prijavi v aplikacijo. Admin in koordinator urejata vse. Organizacija vidi
        razpored, mesta in ljudi ter piše opombe o prostovoljcih — pošte, dnevnika in nastavitev ne
        vidi in ničesar ne spreminja.
      </div>

      <div className="st-users">
        {(list.data ?? []).map((u) => (
          <UserRow key={u.id} user={u} self={u.id === me?.id} />
        ))}
      </div>

      <div className="st-eyebrow">DODAJ DOSTOP</div>
      <div className="st-useradd">
        <TextInput
          size="sm"
          label="E-pošta"
          placeholder="ime@primer.si"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <TextInput
          size="sm"
          label="Ime"
          placeholder="neobvezno"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Select
          size="sm"
          label="Vloga"
          data={ROLE_OPTIONS}
          value={role}
          allowDeselect={false}
          onChange={(v) => v && setRole(v)}
        />
        <Button size="sm" disabled={!validEmail} loading={mut.grant.isPending} onClick={add}>
          Dodaj
        </Button>
      </div>
      <div className="st-hint">
        Prijava je s povezavo na e-pošto, kot za vse ostale. Brez gesla.
      </div>
    </div>
  );
}

function UserRow({ user: u, self }: { user: CoordinatorRow; self: boolean }) {
  const mut = useCoordinatorMutations();
  const [editing, setEditing] = useState<string | null>(null);
  const saveName = () => {
    const name = editing?.trim() ?? '';
    if (!name || name === u.name) return setEditing(null);
    mut.rename.mutate({ id: u.id, name }, { onSuccess: () => setEditing(null) });
  };
  const status = u.disabledAt
    ? `dostop odvzet ${onDate(u.disabledAt)}`
    : !u.canLogIn
      ? 'brez dostopa — ni več na seznamu strežnika'
      : u.lastSeenAt
        ? `nazadnje ${onDate(u.lastSeenAt)}`
        : 'še se ni prijavil';

  return (
    <div className={`st-user${u.disabledAt || !u.canLogIn ? ' st-user--off' : ''}`}>
      <div className="st-user-who">
        <div className="st-user-name">
          {editing !== null ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveName();
              }}
              style={{ display: 'flex', gap: 6, alignItems: 'center' }}
            >
              <TextInput
                id={`user-name-${u.id}`}
                size="xs"
                w={200}
                placeholder="Ime in priimek"
                aria-label="Ime in priimek"
                autoFocus
                maxLength={200}
                value={editing}
                onChange={(e) => setEditing(e.currentTarget.value)}
                onKeyDown={(e) => e.key === 'Escape' && setEditing(null)}
              />
              <Button size="compact-xs" type="submit" disabled={!editing.trim()} loading={mut.rename.isPending}>
                Shrani
              </Button>
              <Button size="compact-xs" variant="default" onClick={() => setEditing(null)}>
                Prekliči
              </Button>
            </form>
          ) : (
            <>
              {u.name.trim() ? u.name : <span className="st-user-noname">brez imena</span>}
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                aria-label={`Uredi ime — ${coordinatorLabel(u)}`}
                title="Uredi ime"
                onClick={() => setEditing(u.name)}
              >
                ✎
              </ActionIcon>
            </>
          )}
          {self ? <span className="st-user-tag">ti</span> : null}
          {u.fromEnv ? (
            <span className="st-user-tag" title="Naslov je na seznamu COORDINATOR_EMAILS na strežniku.">
              strežnik
            </span>
          ) : null}
        </div>
        <div className="st-hint" style={{ marginTop: 0 }}>
          {`${u.email} · `}
          {status}
        </div>
      </div>
      <Select
        size="xs"
        w={140}
        data={ROLE_OPTIONS}
        value={u.role}
        allowDeselect={false}
        // Your own role is not yours to change: it could lock the app away from everyone.
        disabled={self || Boolean(u.disabledAt) || mut.setRole.isPending}
        onChange={(v) => v && v !== u.role && mut.setRole.mutate({ id: u.id, role: v })}
      />
      <div className="st-user-actions">
        {self ? null : u.disabledAt ? (
          <Button size="xs" variant="default" w={118} loading={mut.restore.isPending} onClick={() => mut.restore.mutate(u.id)}>
            Vrni dostop
          </Button>
        ) : (
          <>
            {u.canLogIn ? null : (
              <Button
                size="xs"
                variant="default"
                w={118}
                loading={mut.grant.isPending}
                onClick={() => mut.grant.mutate({ email: u.email, name: u.name, role: u.role })}
              >
                Dodeli dostop
              </Button>
            )}
            {/* Also on a row that can no longer log in: a session opened while the
                address was on the list stays open until it is revoked here. */}
            <Button
              size="xs"
              variant="light"
              color="red"
              w={118}
              loading={mut.revoke.isPending}
              onClick={() => mut.revoke.mutate(u.id)}
            >
              Odvzemi dostop
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
